import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';

type ResourceState<T> = {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => Promise<void>;
  setData: (updater: T | ((current: T) => T)) => void;
};

/**
 * Henter data ved montering og gir en `reload`.
 *
 * `error` keeps the full `ApiError` so pages can tell a locked feature (402)
 * apart from a genuine failure.
 */
export function useResource<T>(
  loader: () => Promise<T>,
  deps: React.DependencyList,
): ResourceState<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(loader, deps);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      setDataState(await load());
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, { code: 'unknown', message: 'Noe gikk galt.' }),
      );
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void run();
  }, [run]);

  const setData = useCallback((updater: T | ((current: T) => T)) => {
    setDataState((current) =>
      typeof updater === 'function'
        ? current === null
          ? current
          : (updater as (value: T) => T)(current)
        : updater,
    );
  }, []);

  return { data, loading, error, reload: run, setData };
}
